import { z } from 'zod';
import { recipeCategorySchema } from '../constants/categories.js';
import { DIETS } from '../constants/diets.js';
import { seasonSchema } from '../constants/seasons.js';
import { ingredientUnitSchema } from '../constants/units.js';
import { userIdSchema } from './user-id.js';
// ---------------------------------------------------------------------------
// Recipe type discriminant
// ---------------------------------------------------------------------------
export const RecipeTypeSchema = z.enum(['recipe', 'dish']);
// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------
export const RecipeSchema = z.object({
    id: z.string().uuid(),
    family_id: z.string().uuid(),
    title: z.string().min(1),
    description: z.string().nullable().default(null),
    type: RecipeTypeSchema,
    category: recipeCategorySchema,
    tags: z.array(z.string()).default([]),
    seasons: z.array(seasonSchema).default([]),
    total_time: z.number().int().positive().nullable().default(null),
    waiting_time: z.number().int().positive().nullable().default(null),
    cooking_time: z.number().int().positive().nullable().default(null),
    baking_time: z.number().int().positive().nullable().default(null),
    servings_value: z.number().int().positive(),
    servings_min: z.number().int().positive().nullable().default(null),
    servings_increment: z.number().int().positive().default(1),
    servings_unit: z.string().default('personnes'),
    servings_text: z.string().nullable().default(null),
    source_name: z.string().nullable().default(null),
    source_url: z.string().url().nullable().default(null),
    /** Said by the family to suit children; the AI menu also judges unflagged ones. */
    kid_friendly: z.boolean().default(false),
    /** The recipe this one declines; null for a base. One level only. */
    variant_of_recipe_id: z.string().uuid().nullable().default(null),
    /** What sets a variant apart (« Sans gluten »); set on a variant only. */
    variant_label: z.string().nullable().default(null),
    /** The diet a variant serves, if it says one. */
    variant_diet: z.enum(DIETS).nullable().default(null),
    created_by: userIdSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    deleted_at: z.string().datetime().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Recipe Ingredient Group
// ---------------------------------------------------------------------------
export const RecipeIngredientGroupSchema = z.object({
    id: z.string().uuid(),
    recipe_id: z.string().uuid(),
    title: z.string().nullable().default(null),
    sort_order: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Recipe Ingredient
// ---------------------------------------------------------------------------
export const RecipeIngredientSchema = z.object({
    id: z.string().uuid(),
    group_id: z.string().uuid(),
    name: z.string().min(1),
    quantity_value: z.number().positive().nullable().default(null),
    quantity_min: z.number().positive().nullable().default(null),
    quantity_max: z.number().positive().nullable().default(null),
    unit: ingredientUnitSchema.nullable().default(null),
    unit_label: z.string().nullable().default(null),
    ingredient_ref_id: z.string().uuid().nullable().default(null),
    sort_order: z.number().int().nonnegative(),
    step_section_id: z.string().uuid().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Recipe Ingredient Alternative
// ---------------------------------------------------------------------------
export const RecipeIngredientAlternativeSchema = z.object({
    id: z.string().uuid(),
    ingredient_id: z.string().uuid(),
    alternative_name: z.string().min(1),
    quantity_value: z.number().positive().nullable().default(null),
    unit: ingredientUnitSchema.nullable().default(null),
    unit_label: z.string().nullable().default(null),
    alternative_recipe_id: z.string().uuid().nullable().default(null),
    notes: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Recipe Step Section
// ---------------------------------------------------------------------------
export const RecipeStepSectionSchema = z.object({
    id: z.string().uuid(),
    recipe_id: z.string().uuid(),
    title: z.string().nullable().default(null),
    sort_order: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Recipe Step
// ---------------------------------------------------------------------------
export const RecipeStepSchema = z.object({
    id: z.string().uuid(),
    section_id: z.string().uuid(),
    instruction: z.string().min(1),
    alternative_text: z.string().nullable().default(null),
    technique_id: z.string().uuid().nullable().default(null),
    duration_seconds: z.number().int().positive().nullable().default(null),
    note: z.string().nullable().default(null),
    /** Step illustration. Column added by 20260228221200_add_image_urls.sql. */
    image_url: z.string().nullable().default(null),
    sort_order: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Recipe Image
// ---------------------------------------------------------------------------
export const RecipeImageSchema = z.object({
    id: z.string().uuid(),
    recipe_id: z.string().uuid(),
    url: z.string().url(),
    is_cover: z.boolean().default(false),
    is_ai_generated: z.boolean().default(false),
    sort_order: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Recipe Version
// ---------------------------------------------------------------------------
export const RecipeVersionSchema = z.object({
    id: z.string().uuid(),
    recipe_id: z.string().uuid(),
    version: z.number().int().positive(),
    data: z.record(z.unknown()),
    changed_by: userIdSchema,
    changed_at: z.string().datetime(),
    change_summary: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Recipe Sub-Recipe (dish → recipe link)
// ---------------------------------------------------------------------------
export const RecipeSubRecipeSchema = z.object({
    id: z.string().uuid(),
    parent_recipe_id: z.string().uuid(),
    child_recipe_id: z.string().uuid(),
    sort_order: z.number().int().nonnegative(),
    is_optional: z.boolean().default(false),
    notes: z.string().nullable().default(null),
});
//# sourceMappingURL=recipe.js.map